<template>
  <el-dialog
    title="从 MinIO 导入模型"
    :visible.sync="innerVisible"
    width="860px"
    @open="loadCandidates"
    @closed="resetForm"
  >
    <el-table
      v-loading="loading"
      :data="candidates"
      :row-style="rowStyle"
      empty-text="MinIO 上没有可导入的训练任务"
      highlight-current-row
      max-height="300"
      border
      size="small"
      style="width:100%"
      @current-change="handlePick"
    >
      <el-table-column label="训练任务" min-width="220" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <span class="mono">{{ row.run_name }}</span>
        </template>
      </el-table-column>
      <el-table-column label="权重" width="90" align="right">
        <template slot-scope="{ row }">{{ formatSize(row.weight_size) }}</template>
      </el-table-column>
      <el-table-column label="训练轮数" width="120" align="center">
        <template slot-scope="{ row }">{{ formatEpochs(row.epochs_done, row.summary.epochs) }}</template>
      </el-table-column>
      <el-table-column label="数据集" min-width="150" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.summary.dataset || '-' }}</template>
      </el-table-column>
      <el-table-column label="归档时间" width="150">
        <template slot-scope="{ row }">{{ formatTime(row.summary.pushed_at || row.weight_mtime) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="120" align="center">
        <template slot-scope="{ row }">
          <el-tag v-if="row.registered" type="info" size="mini">已导入 {{ row.model_id }}</el-tag>
          <el-tag v-else type="success" size="mini">可导入</el-tag>
        </template>
      </el-table-column>
    </el-table>

    <el-form :model="form" label-width="96px" size="small" class="import-form">
      <el-form-item label="所选任务">
        <span v-if="form.run_name" class="mono">{{ form.run_name }}</span>
        <span v-else class="placeholder">请在列表中选择一行</span>
      </el-form-item>
      <el-form-item label="模型名称">
        <el-input v-model="form.name" placeholder="留空则用任务名" clearable />
      </el-form-item>
      <el-form-item label="类别名">
        <el-input v-model="form.class_names" placeholder="逗号分隔，如 pipe,person" clearable />
        <div class="form-tip">默认按类别注册表填，顺序要与训练时一致</div>
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button size="small" @click="innerVisible = false">取消</el-button>
      <el-button
        type="primary"
        size="small"
        :loading="importing"
        :disabled="!form.run_name"
        @click="handleImport"
      >导入</el-button>
    </div>
  </el-dialog>
</template>

<script>
import dayjs from 'dayjs';
import { fetchMinioCandidates, importModelFromMinio } from '../api/blissrose/inference';
import { fetchDatasetClasses } from '../api/dataset';

// 类型只有 yolo 一种，不设选择项
const DEFAULT_FORM = { run_name: '', name: '', model_type: 'yolo', class_names: '' };

/** 从 MinIO 导入模型对话框：best.pt 拉成模型记录；候选每次打开现拉，已导入的标 registered；类别名按类别注册表预填、可改 */
export default {
  name: 'ModelImportDialog',

  props: {
    visible: { type: Boolean, default: false },
    /** 训练页带过来的任务名：打开即选中并预填，省掉在候选列表里找那一步 */
    presetRunName: { type: String, default: '' },
  },

  data() {
    return {
      loading: false,
      importing: false,
      candidates: [],
      // 平台默认类目，随候选列表一起下发；选中 run 时预填进表单
      defaultClassNames: [],
      form: { ...DEFAULT_FORM },
    };
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
  },

  methods: {
    async loadCandidates() {
      this.loading = true;
      try {
        const [candidateRes, classRes] = await Promise.all([
          fetchMinioCandidates(),
          // 类别名以平台类别注册表为准；注册表取不到再退回服务端给的默认类目
          fetchDatasetClasses({ enabled: 1 }).catch(() => null),
        ]);
        const { items, default_class_names: defaultClassNames } = candidateRes.data;
        const registryNames = classRes ? (classRes.data || []).map(item => item.name) : [];
        this.candidates = items || [];
        this.defaultClassNames = registryNames.length ? registryNames : (defaultClassNames || []);
        if (this.presetRunName) this.applyPreset();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.loading = false;
      }
    },

    /** 训练页带过来的任务：候选列表里选中它并预填参数；已导入 / 还没归档的都在提示里说清 */
    applyPreset() {
      const picked = this.candidates.find(item => item.run_name === this.presetRunName);
      if (!picked) {
        this.$message.warning(`MinIO 上还没有「${this.presetRunName}」的产物，先在训练页点「归档产物」`);
        return;
      }
      // 每次带任务进来都从干净的表单起，避免上一次的选择留在框里
      this.form = { ...DEFAULT_FORM };
      this.handlePick(picked);
    },

    // ====== 展示辅助 ======

    /** 已导入的行置灰（el-table 内部行，scoped 样式够不着，用行内样式） */
    rowStyle({ row }) {
      return row.registered ? { color: '#c0c4cc' } : null;
    },

    formatSize(bytes) {
      return bytes ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : '-';
    },

    /** 轮数显示成「实际 / 配置」 */
    formatEpochs(done, configured) {
      const actual = done === null || done === undefined ? '-' : done;
      return `${actual} / ${configured || '-'}`;
    },

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';
    },

    // ====== 操作 ======

    /** 选中一行即预填名称与默认类目；已导入的拦下 */
    handlePick(row) {
      if (!row) return;
      if (row.registered) {
        this.$message.warning(`该任务已导入过模型${row.model_id ? `：${row.model_id}` : ''}`);
        return;
      }
      this.form = {
        ...DEFAULT_FORM,
        run_name: row.run_name,
        class_names: this.defaultClassNames.join(','),
      };
    },

    resetForm() {
      this.form = { ...DEFAULT_FORM };
      this.candidates = [];
      this.defaultClassNames = [];
    },

    async handleImport() {
      this.importing = true;
      try {
        const record = (await importModelFromMinio({
          run_name: this.form.run_name,
          name: this.form.name,
          model_type: this.form.model_type,
          class_names: this.form.class_names.split(',').map(s => s.trim()).filter(Boolean),
        })).data;
        this.$message.success('模型已导入');
        this.innerVisible = false;
        this.$emit('imported', record);
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
.import-form {
  margin-top: 16px;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

.placeholder {
  color: #c0c4cc;
}

.form-tip {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
}
</style>
